<script lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui';
import type { Cell, Column, HeaderContext, RowData } from '@tanstack/table-core';
import type { ColumnOrderState, ColumnPinningOptions, ColumnPinningState, ColumnSizingInfoState, ColumnSizingOptions, ColumnSizingState, ExpandedOptions, ExpandedState, GlobalFilterOptions, PaginationOptions, PaginationState, Row, RowSelectionOptions, RowSelectionState, SortingOptions, SortingState, Updater, VisibilityOptions, VisibilityState } from '@tanstack/vue-table';
import type { VariantProps } from 'tailwind-variants';
import type { UseDynamicScrollerOptions } from 'vue-virtual-scroller';
import type { ThemeSlots } from '#layers/ui/types/tv';
import type { TableColumn, TableData } from '#ui/types';
import { tv } from 'tailwind-variants';

const table = tv({
	slots: {
		root: 'relative flex flex-col overflow-auto overscroll-y-none',
		base: 'relative isolate grid w-max min-w-full auto-rows-min grid-cols-(--table-cols) transition-opacity', // main grid container, w-max allows expansion beyond container
		thead: 'group/thead col-span-full grid auto-rows-min grid-cols-subgrid bg-muted [&>tr>th]:border-b',
		tbody: 'col-span-full grid grid-cols-subgrid [&>tr[data-selectable=true]:not([data-selected=true]):hover>td]:bg-muted',
		tr: `
			group/row relative col-span-full grid grid-cols-subgrid
			data-[selectable=true]:focus-visible:outline-none
			data-[selectable=true]:focus-visible:after:pointer-events-none data-[selectable=true]:focus-visible:after:absolute
			data-[selectable=true]:focus-visible:after:inset-0 data-[selectable=true]:focus-visible:after:z-1
			data-[selectable=true]:focus-visible:after:ring-2 data-[selectable=true]:focus-visible:after:ring-primary
			data-[selectable=true]:focus-visible:after:ring-inset
			[&[data-striped=true]:not([data-selected=true])]:bg-muted
		`,
		th: `
			group/th relative flex items-center truncate bg-muted px-5 py-3 text-start text-sm font-medium whitespace-nowrap
			text-highlighted
			focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary
			data-dnd-dragging:border-x-2 data-dnd-dragging:border-b-0 data-dnd-dragging:border-primary-muted
			rtl:text-right
		`,
		td: `
			relative z-0 flex items-center truncate border-b px-5 text-sm whitespace-nowrap text-default
			group-data-[selected=true]/row:border-b group-data-[selected=true]/row:border-b-primary/15
			group-data-[selected=true]/row:bg-[color-mix(in_srgb,var(--ui-primary)_5%,var(--ui-bg))]
			group-data-[first-row=false]/row:group-data-[selected=true]/row:-mt-px
			group-data-[first-row=false]/row:group-data-[selected=true]/row:border-t
			group-data-[first-row=false]/row:group-data-[selected=true]/row:border-t-primary/15
			dark:group-data-[selected=true]/row:bg-[color-mix(in_srgb,var(--ui-primary)_15%,var(--ui-bg))]
		`,
		expandedRow: 'col-span-full grid grid-cols-subgrid',
		expandedCell: 'overflow-visible border-b border-default whitespace-normal',
		empty: 'bg-default py-6 text-center text-sm text-muted',
	},
	variants: {
		virtualize: {
			false: {
				base: 'overflow-clip',
			},
		},
		sticky: {
			true: {
				thead: 'sticky top-0 z-2',
			},
		},
		/* eslint-disable better-tailwindcss/enforce-logical-properties -- pinned columns use physical `left` offsets, so their divider stays physical. */
		pinned: {
			true: {
				thead: 'bg-muted',
				th: 'sticky z-1 border-r-accented bg-muted [&:not(:has(~_[data-pinned=left]))]:border-r',
				td: `
					sticky z-1 border-r-accented bg-default
					group-data-[selected=true]/row:border-r-primary/25
					[&:not(:has(~_[data-pinned=left]))]:border-r
				`,
			},
		},
		/* eslint-enable better-tailwindcss/enforce-logical-properties -- end of the pinned-column variant. */
		spacing: {
			compact: {
				td: 'py-1.5',
			},
			comfortable: {
				td: 'py-2',
			},
			cozy: {
				td: 'py-4',
			},
		},
		dragging: {
			true: {
				base: '[&>tbody]:opacity-50',
			},
		},
		// A table that fits its container swaps the default `w-max` for `w-full`: nothing can scroll
		// the overflow back into view, so an oversized track would be clipped away by the wrapper
		// instead. The fill column absorbs the slack and truncates.
		fitContainer: {
			true: {
				root: 'overflow-visible',
				base: 'w-full',
			},
		},
	},
});

type TableVariants = VariantProps<typeof table>;

export interface ColumnHeaderActionGroup {
	/** Rendered as one divider-separated group in the header menu. */
	items: DropdownMenuItem[];
	/** Position relative to the built-in sort/visibility groups. Defaults to 'start'. */
	anchor?: 'start' | 'end';
}

declare module '@tanstack/table-core' {
	interface ColumnMeta<TData extends RowData, TValue> {
		onCellClick?: (cell: Cell<TData, TValue>, e?: Event) => void;
		onCellContextMenu?: (cell: Cell<TData, TValue>, e: MouseEvent) => void;
		/** The directions the column may sort by; both when omitted. */
		sortDirections?: Array<SortDirection>;
		headerActions?:
			| ColumnHeaderActionGroup[]
			| ((ctx: HeaderContext<TData, TValue>) => ColumnHeaderActionGroup[]);
	}
}

export type ExtendedTableColumn<T extends TableData, D = unknown> = TableColumn<T, D>;

export interface DataTableBaseProps<T extends RowData> {
	data?: T[];
	columns?: ExtendedTableColumn<T, any>[];
	/**
	 * Whether the table should have a sticky header.
	 * @defaultValue true
	 */
	sticky?: boolean;
	/**
	 * Hold the grid to its container width instead of letting it scroll horizontally, so the
	 * fill column truncates rather than overflowing. Use it for a table embedded in a layout
	 * that does its own scrolling. Ignored while `virtualize` is set, which needs a scroll
	 * container of its own.
	 * @defaultValue false
	 */
	fitContainer?: boolean;
	spacing?: TableVariants['spacing'];
	defaultColumn?: Partial<ExtendedTableColumn<T>>;
	/**
	 * @see [API](https://tanstack.com/table/v8/docs/api/features/column-pinning#table-options)
	 * @see [Guide](https://tanstack.com/table/v8/docs/guide/column-pinning)
	 */
	columnPinningOptions?: Omit<ColumnPinningOptions, 'onColumnPinningChange'>;
	/**
	 * @link [API Docs](https://tanstack.com/table/v8/docs/api/features/column-sizing#table-options)
	 * @link [Guide](https://tanstack.com/table/v8/docs/guide/column-sizing)
	 */
	columnSizingOptions?: Omit<ColumnSizingOptions, 'onColumnSizingChange' | 'onColumnSizingInfoChange'>;
	/**
	 * @link [API Docs](https://tanstack.com/table/v8/docs/api/features/column-visibility#table-options)
	 * @link [Guide](https://tanstack.com/table/v8/docs/guide/column-visibility)
	 */
	visibilityOptions?: Omit<VisibilityOptions, 'onColumnVisibilityChange'>;
	/**
	 * @link [API Docs](https://tanstack.com/table/v8/docs/api/features/global-filtering#table-options)
	 * @link [Guide](https://tanstack.com/table/v8/docs/guide/global-filtering)
	 */
	globalFilterOptions?: Omit<GlobalFilterOptions<T>, 'onGlobalFilterChange'>;
	/**
	 * @link [API Docs](https://tanstack.com/table/v8/docs/api/features/row-selection#table-options)
	 * @link [Guide](https://tanstack.com/table/v8/docs/guide/row-selection)
	 */
	rowSelectionOptions?: Omit<RowSelectionOptions<T>, 'onRowSelectionChange'>;
	/**
	 * @link [API Docs](https://tanstack.com/table/v8/docs/api/features/pagination#table-options)
	 * @link [Guide](https://tanstack.com/table/v8/docs/guide/pagination)
	 */
	paginationOptions?: Omit<PaginationOptions, 'onPaginationChange'>;
	/**
	 * @link [API Docs](https://tanstack.com/table/v8/docs/api/features/sorting#table-options)
	 * @link [Guide](https://tanstack.com/table/v8/docs/guide/sorting)
	 */
	sortingOptions?: Omit<SortingOptions<T>, 'getSortedRowModel' | 'onSortingChange'>;
	/**
	 * Note: requires `getRowId` to be set for stable row identity across data updates.
	 * @link [API Docs](https://tanstack.com/table/v8/docs/api/features/expanding#table-options)
	 * @link [Guide](https://tanstack.com/table/v8/docs/guide/expanding)
	 */
	expandedOptions?: Omit<ExpandedOptions<T>, 'getExpandedRowModel' | 'onExpandedChange'>;

	/**
	 * Enable virtualization for large datasets, backed by `vue-virtual-scroller`'s dynamic scroller.
	 * Note: when enabled, the divider between rows and sticky properties are not supported.
	 * @defaultValue false
	 */
	virtualize?: boolean | (Partial<Omit<UseDynamicScrollerOptions, 'items' | 'keyField' | 'direction'>>);

	/**
	 * Reactive values to watch; when any changes, clears the memoized max column
	 * widths used by virtualize stabilization. Pass reactive sources that indicate
	 * the underlying data set changed (e.g. active filters, search query, or a
	 * content signature when row edits should reopen column sizing).
	 */
	stabilizeColumnSizesDeps?: MaybeRefOrGetter<unknown>[];

	/**
	 * Id of the column that carries the flexible `1fr` track. Defaults to the last
	 * center column; set it when a leading column should absorb the slack instead,
	 * so trailing columns can stay a fixed width flush to the content edge.
	 */
	fillColumn?: string;

	/**
	 * When true, continue observing column widths during virtual scroll and
	 * widen frozen tracks if larger content scrolls into view. Columns never
	 * shrink. When false (default), widths are locked at freeze-time.
	 * @defaultValue false
	 */
	virtualizeAutoSizeOnScroll?: boolean;

	/**
	 * When true, virtualize the horizontally scrollable center columns: only the
	 * center columns intersecting the viewport render body cells, while pinned
	 * columns and the full grid track list are always kept. Only applies while
	 * the table is virtualized and column widths have frozen.
	 * @defaultValue false
	 */
	virtualizeColumns?: boolean;

	/**
	 * Fixed row height in pixels. When set, virtualization uses the RecycleScroller
	 * (O(1) index-based positioning, no per-row measurement) instead of the
	 * DynamicScroller, so vertical scrolling doesn't thrash layout. Only pass this
	 * when every row is guaranteed to be this height (no variable-height/expanded
	 * rows). Omit for variable-height rows.
	 */
	fixedRowHeight?: number;

	getRowId?: (originalRow: T) => string;
	onSelect?: (row: Row<T>, e?: Event) => void;
	class?: any;
	striped?: boolean;
	/**
	 * Render an expanded row as one full-width cell (grid-column `1 / -1`) instead of the default
	 * pinned-aware split (left/right placeholder cells around a centered cell). Use when the expanded
	 * content should bleed under the pinned leading columns — e.g. a shaded panel in a non-scrolling
	 * table. Defaults to the split rendering, so existing consumers are unaffected.
	 */
	expandedFullWidth?: boolean;
	ui?: ThemeSlots<typeof table>;
}

export interface DataTableBaseAdditionalProps {
	pagination?: PaginationState;
	sorting?: SortingState;
	columnOrder?: ColumnOrderState;
	columnPinning?: ColumnPinningState;
	columnSizing?: ColumnSizingState;
	columnSizingInfo?: ColumnSizingInfoState;
	columnVisibility?: VisibilityState;
	rowSelection?: RowSelectionState;
	expanded?: ExpandedState;
	globalFilter?: string;
}

export interface DataTableBaseEmits {
	'update:pagination': [pagination: PaginationState];
	'update:sorting': [sorting: SortingState];
	'update:columnOrder': [columnOrder: ColumnOrderState];
	'update:columnPinning': [columnPinning: ColumnPinningState];
	'update:columnSizing': [columnSizing: ColumnSizingState];
	'update:columnSizingInfoState': [columnSizingInfo: ColumnSizingInfoState];
	'update:columnVisibility': [columnVisibility: VisibilityState];
	'update:rowSelection': [rowSelection: RowSelectionState];
	'update:expanded': [expanded: ExpandedState];
	'update:globalFilter': [globalFilter: string];
}

declare module '@tanstack/vue-table' {
	interface ColumnSizingInfoState {
		isHoveringHandle?: string | boolean;
	}
}
</script>

<script setup lang="ts" generic="T extends RowData">
import type { DragEndEvent, DragOverEvent } from '@dnd-kit/abstract';
import type { ComponentPublicInstance, ComputedRef, CSSProperties, MaybeRefOrGetter, ObjectDirective, Ref } from 'vue';
import type { SortDirection } from '#layers/base/public';
import type { DataTableColumnPinGroup } from './data-table-fill-column';
import { PointerActivationConstraints, PointerSensor } from '@dnd-kit/dom';
import { move } from '@dnd-kit/helpers';
import { DragDropProvider } from '@dnd-kit/vue';
import { FlexRender, getCoreRowModel, getExpandedRowModel, getFilteredRowModel, getSortedRowModel, useVueTable } from '@tanstack/vue-table';
import { computedWithControl, createReusableTemplate, unrefElement, useElementBounding, useResizeObserver, useScroll } from '@vueuse/core';
import { Primitive } from 'reka-ui';
import { computed, nextTick, onScopeDispose, ref, useSlots, useTemplateRef, watch, watchEffect } from 'vue';
import { useDynamicScroller, useRecycleScroller } from 'vue-virtual-scroller';
import { callOrReturn } from '#layers/base/public';
import { ColumnOrderingExtension } from '#layers/ui/utils/table/features/column-ordering-extension';
import { AUTO_COLUMN_MAX_WIDTH } from './data-table-column-width';
import { getReorderableFillColumnId, selectFillColumnId } from './data-table-fill-column';
import { useDataTableColumnFreezeWindow } from './use-data-table-column-freeze-window';
import { useDataTableColumnResize } from './use-data-table-column-resize';
import { useDataTableColumnWindow } from './use-data-table-column-window';

defineOptions({
	inheritAttrs: false,
});

const {
	spacing = 'comfortable',
	sticky = true,
	defaultColumn = {
		enableSorting: false,
		enableHiding: false,
		enableOrdering: false,
		enablePinning: false,
		size: 0, // auto-size by default
		minSize: 56,
	},
	data: dataProp,
	columns,
	virtualize,
	fitContainer,
	paginationOptions,
	columnPinningOptions,
	sortingOptions,
	expandedOptions,
	visibilityOptions,
	columnSizingOptions,
	rowSelectionOptions,
	globalFilterOptions,
	getRowId,
	onSelect,
	fillColumn,
	stabilizeColumnSizesDeps,
	virtualizeColumns,
	virtualizeAutoSizeOnScroll,
	fixedRowHeight,
	ui: uiProp,
	class: className,
} = defineProps<DataTableBaseProps<T>>();

const slots = useSlots();
const hasExpandedSlot = computed(() => !!slots.expanded);

/**
 * Source-of-truth array passed to useVueTable.
 *
 * Previously this was a shallowRef + deep watcher that copied `props.data`
 * into a local array on every change. That pattern was observed in staging to
 * drop the skeleton→real-data transition: cells kept rendering skeleton rows
 * even after `props.data` had flipped to real items. Using a computed removes
 * the intermediate ref and guarantees TanStack observes the current props.
 */
const data = computed(() => {
	const items = dataProp ?? [];
	// Return a fresh array reference each time so TanStack's data-change
	// detection always sees a new identity on swap (skeleton→real), even if
	// consumers accidentally reuse the same array instance.
	return [...items];
}) as ComputedRef<T[]>;
const tableColumns = computed(() => columns ?? []);

const ui = computed(() => table({
	sticky,
	spacing,
	virtualize: !!virtualize,
	dragging: isDragging.value,
	// Dropped while virtualized: the fit swaps away the scroll container virtualization recycles rows
	// against. Virtualization wins, because silently un-virtualizing a table that asked for it is the
	// worse failure.
	fitContainer: fitContainer && !virtualize,
}));

const paginationState = defineModel<PaginationState>('pagination', {
	default: () => ({
		pageIndex: 0,
		pageSize: 10,
	}),
});
const sortingState = defineModel<SortingState>('sorting');
const columnOrderState = defineModel<ColumnOrderState>('columnOrder');
const columnPinningState = defineModel<ColumnPinningState>('columnPinning');
const columnSizingState = defineModel<ColumnSizingState>('columnSizing');
const columnSizingInfoState = defineModel<ColumnSizingInfoState>('columnSizingInfo');
const columnVisibilityState = defineModel<VisibilityState>('columnVisibility');
const rowSelectionState = defineModel<RowSelectionState>('rowSelection');
const expandedState = defineModel<ExpandedState>('expanded');
const globalFilterState = defineModel<string>('globalFilter');

const rootRef = useTemplateRef<ComponentPublicInstance>('root');
const visibleTableRef = useTemplateRef<HTMLTableElement>('visibleTable');
const rootBounding = useElementBounding(rootRef);
const rootScroll = useScroll(() => unrefElement(rootRef));
// Track whether the rows end at the table's bottom edge, with no empty space below them.
const rowsReachBottom = ref(false);

function updateRowsReachBottom() {
	const root = unrefElement(rootRef.value);
	const table = visibleTableRef.value;
	// Compare layout heights so transforms can't skew the result, allowing 1px for rounding.
	rowsReachBottom.value = !!root && !!table && table.offsetHeight >= root.clientHeight - 1;
}

useResizeObserver([
	computed(() => unrefElement(rootRef.value)),
	visibleTableRef,
], updateRowsReachBottom);

// eslint-disable-next-line vue/no-setup-props-reactivity-loss -- table options and dependency list are fixed at setup; reactive inputs are passed as getters or refs
const tableApi = useVueTable({
	_features: [ColumnOrderingExtension],
	data,
	get columns() {
		return tableColumns.value;
	},
	defaultColumn: defaultColumn ?? [],
	columnResizeMode: 'onChange',
	getCoreRowModel: getCoreRowModel(),
	getFilteredRowModel: getFilteredRowModel(),
	...paginationOptions,
	onPaginationChange: (updaterOrValue: any) => valueUpdater(updaterOrValue, paginationState),
	getSortedRowModel: getSortedRowModel(),
	...columnPinningOptions,
	onColumnPinningChange: (updaterOrValue: any) => valueUpdater(updaterOrValue, columnPinningState),
	...sortingOptions,
	onSortingChange: (updaterOrValue) => valueUpdater(updaterOrValue, sortingState),
	...expandedOptions,
	getRowCanExpand,
	getExpandedRowModel: getExpandedRowModel(),
	onExpandedChange: (updaterOrValue) => valueUpdater(updaterOrValue, expandedState),
	...visibilityOptions,
	onColumnVisibilityChange: (updaterOrValue) => valueUpdater(updaterOrValue, columnVisibilityState),
	onColumnOrderChange: (updaterOrValue) => valueUpdater(updaterOrValue, columnOrderState),
	...columnSizingOptions,
	onColumnSizingChange: (updaterOrValue) => valueUpdater(updaterOrValue, columnSizingState),
	onColumnSizingInfoChange: (updaterOrValue) => {
		const prevValue = columnSizingInfoState.value;
		const newValue = typeof updaterOrValue === 'function'
			? updaterOrValue(prevValue ?? {} as ColumnSizingInfoState)
			: updaterOrValue;

		// Patch startSize when resize starts on an auto-sized column
		if (newValue?.isResizingColumn && !prevValue?.isResizingColumn && headerRef.value) {
			const column = tableApi.getColumn(newValue.isResizingColumn);

			if (column && isColumnAutoSized(newValue.isResizingColumn)) {
				// Column is auto-sized, measure actual DOM width
				const columnIndex = visibleColumns.value.findIndex((h) => h.id === newValue.isResizingColumn);
				const ths = headerRef.value.querySelectorAll('th');
				const th = ths[columnIndex];

				if (th) {
					const actualWidth = th.getBoundingClientRect().width;

					// Patch the startSize and columnSizingStart
					newValue.startSize = actualWidth;

					if (newValue.columnSizingStart) {
						const idx = newValue.columnSizingStart.findIndex(([id]) => id === newValue.isResizingColumn);

						if (idx !== -1) {
							newValue.columnSizingStart[idx]![1] = actualWidth;
						}
					}
				}
			}
		}

		columnSizingInfoState.value = newValue;
	},
	...rowSelectionOptions,
	onRowSelectionChange: (updaterOrValue) => valueUpdater(updaterOrValue, rowSelectionState),
	...globalFilterOptions,
	onGlobalFilterChange: (updaterOrValue) => valueUpdater(updaterOrValue, globalFilterState),
	getRowId,
	state: {
		get columnOrder() {
			return columnOrderState.value;
		},
		get columnSizing() {
			return columnSizingState.value;
		},
		get columnPinning() {
			return columnPinningState.value;
		},
		get columnSizingInfo() {
			return columnSizingInfoState.value;
		},
		get columnVisibility() {
			return columnVisibilityState.value;
		},
		get globalFilter() {
			return globalFilterState.value;
		},
		get expanded() {
			return expandedState.value;
		},
		get pagination() {
			return paginationState.value;
		},
		get rowSelection() {
			return rowSelectionState.value;
		},
		get sorting() {
			return sortingState.value;
		},
	},
});

// TanStack's table methods are memoized and not Vue-reactive, and its adapter
// reads options lazily — so under Vue 3.5 a changed `data` computed never
// reaches `table.options.data`. Push it in via `setOptions` so the row model
// re-reads it.
watch(data, (newData, oldData) => {
	if (oldData && haveSameRowIdentities(newData, oldData)) {
		return;
	}

	tableApi.setOptions((prev) => ({ ...prev, data: newData }));
}, { flush: 'sync', immediate: true });

// Same gap for `columns`: without this, adding/removing a column never busts
// the header memo chain (`getAllColumns → getFlatHeaders`), so the header and
// CSS grid stay stale until reload. `setOptions` busts it synchronously.
watch(tableColumns, (cols) => {
	tableApi.setOptions((prev) => ({ ...prev, columns: cols }));
}, { flush: 'sync' });

// `getRowModel()` is memoized and not Vue-reactive; declare its inputs so
// `computedWithControl` re-derives the rows when they change.
const rows = computedWithControl(
	[data, columnOrderState, columnVisibilityState, sortingState, expandedState, globalFilterState, paginationState],
	() => tableApi.getRowModel().rows,
);
const firstRowId = computed(() => rows.value.at(0)?.id);
const lastRowId = computed(() => rows.value.at(-1)?.id);
const isVirtualized = computed(() => !!virtualize);

function isFirstRow(row: Row<T>): boolean {
	return row.id === firstRowId.value;
}

/**
 * Returns the class that drops the body's last line once the rows reach the table's bottom edge,
 * so the frame or footer below draws the only line there. An expanded last row ends with its
 * expanded content, so the line between the row and that content stays.
 */
function getBottomEdgeClass(row: Row<T>, line: 'row' | 'expanded'): string {
	const isLastLine = line === 'expanded' || !(hasExpandedSlot.value && row.getIsExpanded());
	return rowsReachBottom.value && row.id === lastRowId.value && isLastLine ? 'border-b-0!' : '';
}

watch(rows, () => {
	void nextTick(updateRowsReachBottom);
}, { flush: 'post', immediate: true });

function valueUpdater<T extends Updater<any>>(updaterOrValue: T, ref: Ref) {
	ref.value = typeof updaterOrValue === 'function' ? updaterOrValue(ref.value) : updaterOrValue;
}

/**
 * Returns true when both arrays contain the same row object identities in the
 * same order.
 */
function haveSameRowIdentities(nextRows: T[], prevRows: T[]) {
	if (nextRows.length !== prevRows.length) {
		return false;
	}

	return nextRows.every((row, index) => Object.is(row, prevRows[index]));
}

function handleRowSelect(row: Row<T>, e: Event) {
	if (!onSelect) {
		return;
	}

	const target = e.target as HTMLElement;
	const isInteractive = target.closest('button') || target.closest('a');

	if (isInteractive) {
		return;
	}

	e.preventDefault();
	e.stopPropagation();

	onSelect(row, e);
}

// drag and drop refs
const headerRef = useTemplateRef('header');
// `getFlatHeaders()` is memoized and not Vue-reactive; declare the inputs that
// restructure the header set so it (and the CSS grid derived from it) re-derive.
// Sizing is excluded on purpose — it doesn't change structure and churns on
// every resize frame.
const visibleColumns = computedWithControl(
	[tableColumns, columnVisibilityState, columnOrderState, columnPinningState],
	() => tableApi.getFlatHeaders().filter((h) => h.column.getIsVisible()),
);

// dnd-kit column reorder
const dndSensors = [
	PointerSensor.configure({
		activationConstraints: [
			new PointerActivationConstraints.Distance({ value: 5 }),
		],
	}),
];

const isResizingColumn = computed(() => !!columnSizingInfoState.value?.isResizingColumn);

/**
 * TanStack renders columns in three partitions: left-pinned, center, and
 * right-pinned. `columnOrder` only controls the center partition; pinned
 * ordering comes from `columnPinning.left/right`.
 */
const columnPinGroupMap = computed(() => new Map(visibleColumns.value.map((header) => [
	header.id,
	getColumnPinGroup(header.column),
])));

// DnD Kit indexes must match the partition being dragged. With one global
// index, DnD Kit can preview an unpinned header before a pinned header while
// TanStack still renders the body from separate pin partitions.
const orderableIndexMap = computed(() => {
	const map = new Map<string, number>();
	const nextIndexByGroup: Record<DataTableColumnPinGroup, number> = {
		center: 0,
		left: 0,
		right: 0,
	};

	for (const header of visibleColumns.value) {
		if (header.column.getCanReorder()) {
			const pinGroup = getColumnPinGroup(header.column);
			map.set(header.id, nextIndexByGroup[pinGroup]);
			nextIndexByGroup[pinGroup]++;
		}
	}

	return map;
});

const isDragging = ref(false);
// Live preview is produced by committing state on `drag-over`. The same move
// must not run again on `drag-end`, because it would be applied to the already
// reordered state and effectively undo the preview.
const didReorderColumnDuringDrag = ref(false);

/**
 * Resets per-drag bookkeeping before DnD Kit starts moving headers.
 */
function handleDragStart() {
	isDragging.value = true;
	didReorderColumnDuringDrag.value = false;
}

/**
 * Handles drop-only reorders when the drag ended before any `drag-over` move.
 */
function handleDragEnd(event: DragEndEvent) {
	isDragging.value = false;

	if (!didReorderColumnDuringDrag.value) {
		handleColumnDrag(event);
	}
}

/**
 * Keeps DnD Kit and TanStack in the same partition while the pointer moves.
 */
function handleColumnDragOver(event: DragOverEvent) {
	const sourceId = getDragColumnId(event.operation.source?.id);
	const targetId = getDragColumnId(event.operation.target?.id);

	if (!sourceId || !targetId) {
		return;
	}

	if (columnPinGroupMap.value.get(sourceId) !== columnPinGroupMap.value.get(targetId)) {
		// Reordering is not a pin/unpin action. Crossing this boundary would let
		// the header preview move into another partition while the body stays in
		// TanStack's original pin partition.
		event.preventDefault();
		return;
	}

	if (sourceId === targetId) {
		return;
	}

	handleColumnDrag(event);
	didReorderColumnDuringDrag.value = true;
}

/**
 * Applies a reorder to the TanStack state field that owns the source partition.
 */
function handleColumnDrag(event: DragEndEvent | DragOverEvent) {
	const sourceId = getDragColumnId(event.operation.source?.id);
	const targetId = getDragColumnId(event.operation.target?.id);
	const sourcePinGroup = sourceId ? columnPinGroupMap.value.get(sourceId) : undefined;

	if (!sourcePinGroup || !targetId || columnPinGroupMap.value.get(targetId) !== sourcePinGroup) {
		return;
	}

	if (sourceId === targetId) {
		return;
	}

	const groupColumns = visibleColumns.value.filter((header) => getColumnPinGroup(header.column) === sourcePinGroup);
	const visibleGroupOrder = groupColumns.map((header) => header.id);
	const nextGroupOrder = getReorderedColumnGroupOrder(groupColumns, event);

	if (sourcePinGroup === 'center') {
		// Center columns are the only columns TanStack reorders from `columnOrder`.
		tableApi.setColumnOrder(mergeVisibleGroupOrder(
			columnOrderState.value,
			visibleGroupOrder,
			nextGroupOrder,
		));

		return;
	}

	// Pinned columns ignore `columnOrder`; their order is stored on the pinning
	// side itself.
	tableApi.setColumnPinning({
		...columnPinningState.value,
		[sourcePinGroup]: mergeVisibleGroupOrder(
			columnPinningState.value?.[sourcePinGroup],
			visibleGroupOrder,
			nextGroupOrder,
		),
	});
}

/**
 * Builds the next visible order for the active pin partition.
 */
function getReorderedColumnGroupOrder(groupColumns: typeof visibleColumns.value, event: DragEndEvent | DragOverEvent) {
	// DnD Kit receives only reorderable columns, so fixed columns such as select
	// or add-field controls cannot be displaced by a drag.
	const orderableColumns = groupColumns.filter((header) => header.column.getCanReorder());
	const reorderedOrderableIds = move(orderableColumns, event).map((header) => header.id);
	return buildGroupColumnOrder(groupColumns, reorderedOrderableIds);
}

/**
 * Merges reordered ids back into a partition, leaving fixed columns in place.
 */
function buildGroupColumnOrder(columns: typeof visibleColumns.value, reorderedOrderableIds: string[]) {
	const nextOrder: string[] = [];
	let orderableIndex = 0;

	for (const column of columns) {
		if (!column.column.getCanReorder()) {
			nextOrder.push(column.id);
			continue;
		}

		const nextColumnId = reorderedOrderableIds[orderableIndex];
		orderableIndex++;

		if (nextColumnId) {
			nextOrder.push(nextColumnId);
		}
	}

	return nextOrder;
}

/**
 * Converts TanStack's pinning value into the sortable partition name.
 */
function getColumnPinGroup(column: Column<T>): DataTableColumnPinGroup {
	const pinning = column.getIsPinned();
	// The UI currently exposes left pinning, but the public table state can still
	// contain right-pinned columns. They need their own partition for the same
	// reason left-pinned columns do.
	return pinning === 'left' || pinning === 'right' ? pinning : 'center';
}

/**
 * Returns a column id from DnD Kit identifiers.
 */
function getDragColumnId(id: unknown): string | undefined {
	if (typeof id === 'string') {
		return id;
	}

	if (typeof id === 'number') {
		return String(id);
	}

	return undefined;
}

/**
 * Replaces the visible ids for one partition without dropping persisted hidden ids.
 */
function mergeVisibleGroupOrder(previousIds: string[] | undefined, visibleGroupIds: string[], nextGroupIds: string[]) {
	if (!previousIds?.length) {
		return nextGroupIds;
	}

	const allVisibleIds = new Set(visibleColumns.value.map((header) => header.id));
	const visibleGroupIdSet = new Set(visibleGroupIds);
	const remainingGroupIds = [...nextGroupIds];
	const nextIds: string[] = [];

	for (const id of previousIds) {
		if (visibleGroupIdSet.has(id)) {
			// Swap visible ids in their existing slots. Other ids are kept below so
			// hidden columns preserve their saved order when shown again.
			const nextGroupId = remainingGroupIds.shift();

			if (nextGroupId) {
				nextIds.push(nextGroupId);
			}

			continue;
		}

		if (!allVisibleIds.has(id)) {
			nextIds.push(id);
		}
	}

	nextIds.push(...remainingGroupIds);
	return nextIds;
}

const lastLeftPinnedColumnId = computed(() => {
	const leftPinned = visibleColumns.value.filter((header) => header.column.getIsPinned() === 'left');
	return leftPinned.at(-1)?.id;
});

const fillColumnCandidates = computed(() => visibleColumns.value.map((header) => ({
	id: header.id,
	pinGroup: getColumnPinGroup(header.column),
	canReorder: header.column.getCanReorder(),
})));

// Unless a table names one, the fill (`1fr`) rides the last center column so it
// can't teleport between data columns during a reorder. On surfaces with
// `$add-field` that is the locked trailing column; elsewhere it is the last data
// column (full-bleed, unchanged).
const fillColumnId = computed(() => fillColumn ?? selectFillColumnId(fillColumnCandidates.value));

if (import.meta.dev) {
	watchEffect(() => {
		// An explicitly named fill column cannot teleport during a reorder, which is the only thing
		// the trailing-column invariant exists to prevent.
		if (fillColumn) return;

		const reorderableFillColumnId = getReorderableFillColumnId(fillColumnCandidates.value);

		if (reorderableFillColumnId) {
			console.warn(
				`[DataTable] Column reorder is enabled but the fill column "${reorderableFillColumnId}" is reorderable. `
				+ 'The last center column must be non-reorderable (set `enableOrdering: false`, as `$add-field` does) '
				+ 'or column reorder will flake.',
			);
		}
	});
}

// eslint-disable-next-line vue/no-setup-props-reactivity-loss -- table options and dependency list are fixed at setup; reactive inputs are passed as getters or refs
const freezeWindow = useDataTableColumnFreezeWindow<T>({
	enabled: isVirtualized,
	visibleColumns,
	rows,
	columnSizing: columnSizingState,
	dependencies: stabilizeColumnSizesDeps,
	isColumnAutoSized,
	headerElement: headerRef,
	isResizingColumn,
	onColumnAutoFit: (columnId, width) => {
		tableApi.setColumnSizing((prev) => ({ ...prev, [columnId]: width }));
	},
});

const {
	layoutPhase,
	resolvedColumnWidths,
	isColumnRelaxed,
	isColumnAutoFitting: isVirtualColumnAutoFitting,
	autoFitColumn,
	resetColumnToRelaxed,
	growFrozenColumnWidths,
} = freezeWindow;

const renderedNonVirtualColumnWidths = ref<Record<string, number>>({});
const renderedNonVirtualHeaders = computed(() => {
	if (isVirtualized.value || !headerRef.value) return [];

	const visibleColumnIds = new Set(visibleColumns.value.map((header) => header.id));

	return [...headerRef.value.querySelectorAll<HTMLElement>('th[data-column-id]')]
		.filter((header) => header.dataset.columnId && visibleColumnIds.has(header.dataset.columnId));
});

function updateRenderedNonVirtualColumnWidths(headers: Iterable<HTMLElement>, replace = false) {
	const currentWidths = renderedNonVirtualColumnWidths.value;
	const widths: Record<string, number> = replace ? {} : { ...currentWidths };
	let changed = false;

	for (const header of headers) {
		const columnId = header.dataset.columnId;
		const width = header.getBoundingClientRect().width;

		if (columnId && width > 0) {
			widths[columnId] = width;
			changed ||= currentWidths[columnId] !== width;
		}
	}

	if (replace && Object.keys(widths).length !== Object.keys(currentWidths).length) {
		changed = true;
	}

	if (changed) {
		renderedNonVirtualColumnWidths.value = widths;
	}
}

watch(renderedNonVirtualHeaders, (headers) => {
	updateRenderedNonVirtualColumnWidths(headers, true);
}, { immediate: true, flush: 'post' });

useResizeObserver(renderedNonVirtualHeaders, (entries) => {
	updateRenderedNonVirtualColumnWidths(entries.map((entry) => entry.target as HTMLElement));
});

const layoutColumnWidths = computed<Record<string, number>>(() => {
	if (isVirtualized.value) return resolvedColumnWidths.value;

	return Object.fromEntries(visibleColumns.value.map((header) => [
		header.id,
		renderedNonVirtualColumnWidths.value[header.id] ?? resolvedColumnWidths.value[header.id] ?? header.getSize(),
	]));
});

const nonVirtualAutoFittingColumnIds = ref(new Set<string>());

function isColumnAutoFitting(columnId: string): boolean {
	return isVirtualColumnAutoFitting(columnId) || nonVirtualAutoFittingColumnIds.value.has(columnId);
}

function setNonVirtualColumnAutoFitting(columnId: string, active: boolean) {
	const next = new Set(nonVirtualAutoFittingColumnIds.value);

	if (active) {
		next.add(columnId);
	}
	else {
		next.delete(columnId);
	}

	nonVirtualAutoFittingColumnIds.value = next;
}

/**
 * Clears a column's manual width and re-measures it back to the clamped
 * automatic width.
 */
function resetColumnWidth(columnId: string) {
	tableApi.setColumnSizing((prev) => {
		if (!prev) return {};
		const { [columnId]: _omitted, ...rest } = prev;
		return rest;
	});

	resetColumnToRelaxed(columnId);
}

/**
 * Fits a column to its full content and persists that width as a manual override.
 * The virtualized path temporarily opens its uncapped measurement window; an
 * ordinary table already renders every row, so its intrinsic scroll widths are
 * available immediately after the manual override is cleared.
 */
function autoFitColumnToContent(columnId: string) {
	if (isVirtualized.value) {
		autoFitColumn(columnId);
		return;
	}

	setNonVirtualColumnAutoFitting(columnId, true);

	void nextTick(() => {
		try {
			const columnIdSelector = CSS.escape(columnId);
			const header = headerRef.value?.querySelector<HTMLElement>(`th[data-column-id="${columnIdSelector}"]`);
			const root = unrefElement(rootRef.value);
			const cells = root?.querySelectorAll<HTMLElement>(`td[data-column-id="${columnIdSelector}"]`) ?? [];
			const minSize = tableApi.getColumn(columnId)?.columnDef.minSize ?? 0;
			let width = Math.max(minSize, header?.scrollWidth ?? 0);

			for (const cell of cells) {
				width = Math.max(width, cell.scrollWidth);
			}

			if (width > 0) {
				tableApi.setColumnSizing((previous) => ({ ...previous, [columnId]: width }));
			}
		}
		finally {
			setNonVirtualColumnAutoFitting(columnId, false);
		}
	});
}

const {
	canColumnResize,
	getResizeHandleStyle,
	onResizeMouseDown,
	onResizeDoubleClick,
	resizeIndicatorInsets,
} = useDataTableColumnResize<T>({
	table: tableApi,
	visibleColumns,
	fillColumnId,
	resolvedColumnWidths: layoutColumnWidths,
	resolvedPinnedOffset: getResolvedPinnedOffset,
	headerElement: headerRef,
	rootElement: () => unrefElement(rootRef.value),
	rootScroll: () => ({ x: rootScroll.x.value, y: rootScroll.y.value }),
	onAutoFitColumn: autoFitColumnToContent,
});

// Center-column (horizontal) virtualization. Only windows once the table is
// virtualized and widths have frozen — while relaxed, every cell must stay
// mounted so the freeze window can measure each column's content width.
const columnWindowEnabled = computed(() =>
	isVirtualized.value && !!virtualizeColumns && layoutPhase.value === 'frozen',
);

const {
	active: columnWindowActive,
	isColumnRendered: isColumnCellRendered,
	getColumnGridIndex,
} = useDataTableColumnWindow<T>({
	enabled: columnWindowEnabled,
	visibleColumns,
	resolvedColumnWidths,
	getColumnPinGroup,
	scrollLeft: () => rootScroll.x.value,
	viewportWidth: () => rootBounding.width.value,
});

/**
 * `undefined` data means callers have not resolved the first load yet. A real
 * array, including `[]`, means the table has loaded data and headers can render.
 */
// eslint-disable-next-line vue/no-setup-props-reactivity-loss -- table options and dependency list are fixed at setup; reactive inputs are passed as getters or refs
const shouldDeferInitialHeaderContent = ref(dataProp == null);

watch(() => dataProp, (items) => {
	if (items != null) {
		shouldDeferInitialHeaderContent.value = false;
	}
}, { flush: 'sync', immediate: true });

const useIntrinsicHeaderSizing = computed(() =>
	isVirtualized.value
	&& dataProp != null
	&& data.value.length === 0,
);

const headerContentVisible = computed(() =>
	!isVirtualized.value
	|| !shouldDeferInitialHeaderContent.value,
);

const cssTableCols = computed(() => visibleColumns.value.map((header) => {
	const size = header.getSize();
	const minSize = header.column.columnDef.minSize ?? 0;
	const isFillColumn = header.id === fillColumnId.value;
	const isAutoSize = isColumnAutoSized(header.id);
	const relaxed = isAutoSize && isColumnRelaxed(header.id);

	if (isFillColumn) {
		if (!isAutoSize) return `minmax(${size}px, 1fr)`;

		if (!isVirtualized.value) {
			return `minmax(${minSize}px, 1fr)`;
		}

		if (relaxed || useIntrinsicHeaderSizing.value) {
			// Keep loaded-empty tables sized from header labels; there are no body
			// cells to widen tracks before the freeze window has anything to commit.
			return `minmax(${minSize}px, 1fr)`;
		}

		const frozen = resolvedColumnWidths.value[header.id] ?? minSize;
		return `minmax(${frozen}px, 1fr)`;
	}

	if (!isAutoSize) return `${size}px`;

	if (!isVirtualized.value) {
		return `minmax(${minSize}px, max-content)`;
	}

	if (relaxed || useIntrinsicHeaderSizing.value) {
		// An explicit auto-fit measures the full, uncapped content width before
		// persisting it as a manual width.
		if (isColumnAutoFitting(header.id)) {
			return 'max-content';
		}

		// Auto columns size to content between the `minSize` floor and the auto
		// ceiling. The ceiling (and clipping of unbreakable content) is enforced by
		// the relaxed cell's `max-width`/`overflow: clip`, not the track, so the
		// `minmax` floor is preserved.
		return `minmax(${minSize}px, max-content)`;
	}

	const frozen = resolvedColumnWidths.value[header.id] ?? minSize;

	// When the grow-only auto-size-on-scroll feature is active we still need the
	// track to be free to expand beyond the frozen floor as wider content scrolls
	// into view. Otherwise the explicit px value locks the column width.
	if (isVirtualized.value && virtualizeAutoSizeOnScroll) {
		return `minmax(${frozen}px, max-content)`;
	}

	return `${frozen}px`;
}).join(' '));

function isColumnAutoSized(columnId: string): boolean {
	if (columnSizingState.value != null && columnId in columnSizingState.value) return false;
	const column = tableApi.getColumn(columnId);
	if (!column) return true;
	return column.getSize() <= (column.columnDef.minSize ?? 0);
}

/**
 * Frozen virtual tracks already hold the capped measurement. The cap needs to
 * live on cells only while the virtualized table is measuring, or for the whole
 * lifetime of a non-virtualized table that uses max-content tracks directly.
 */
function isColumnAutoCapped(columnId: string): boolean {
	return columnId !== fillColumnId.value
		&& isColumnAutoSized(columnId)
		&& (!isVirtualized.value || isColumnRelaxed(columnId));
}

const autoCappedColumnIds = computed(() => new Set(
	visibleColumns.value
		.filter((header) => isColumnAutoCapped(header.id))
		.map((header) => header.id),
));

function getColumnStyles(column: Column<T>): CSSProperties {
	const styles: CSSProperties = {};

	const pinned = column.getIsPinned();

	if (pinned === 'left') {
		styles.left = `${getResolvedPinnedOffset(column, 'left')}px`;
	}
	else if (pinned === 'right') {
		styles.right = `${getResolvedPinnedOffset(column, 'right')}px`;
	}

	return styles;
}

/**
 * Pinned offset resolved against the rendered or frozen column widths instead
 * of the tanstack `getSize()` values, so sticky positioning stays aligned with
 * both intrinsic and explicit CSS grid tracks.
 */
function getResolvedPinnedOffset(column: Column<T>, side: 'left' | 'right'): number {
	const pinned = side === 'left'
		? tableApi.getLeftVisibleLeafColumns()
		: tableApi.getRightVisibleLeafColumns().toReversed();

	let offset = 0;

	for (const current of pinned) {
		if (current.id === column.id) break;
		offset += layoutColumnWidths.value[current.id] ?? current.getSize();
	}

	return offset;
}

function getRowCanExpand(row: Row<T>) {
	return expandedOptions?.getRowCanExpand?.(row)
		?? ((expandedOptions?.enableExpanding ?? true) && (hasExpandedSlot.value || !!row.subRows?.length));
}

defineExpose({
	get $el() {
		return rootRef.value?.$el as HTMLElement;
	},
	tableApi,
	resetColumnWidth,
	getResolvedPinnedOffset,
	scrollToTop() {
		rootRef.value?.$el.scrollTo({
			top: 0,
		});
	},
});

const [DefineTableTemplate, ReuseTableTemplate] = createReusableTemplate();
const [DefineRowTemplate, ReuseRowTemplate] = createReusableTemplate<{ row: Row<T> }>({
	props: {
		row: {
			type: Object,
			required: true,
		},
	},
});

// Virtual scrolling

const scrollerHtmlEl = computed(() => unrefElement(rootRef.value) as HTMLElement);

const {
	pool,
	visiblePool,
	startSpacerSize,
	endSpacerSize,
	vRowItem,
} = createRowScroller();

/**
 * Fixed-height rows (`fixedRowHeight` set) use the RecycleScroller: it positions
 * rows by `index * itemSize` (O(1)) and never measures them, so scrolling does
 * not force a layout read/write per row. Variable-height rows fall back to the
 * DynamicScroller, which measures each row via ResizeObserver.
 *
 * `vRowItem` is the DynamicScroller's per-row measurement directive; the fixed
 * path returns a no-op directive since fixed rows need no measurement.
 */
function sharedScrollerOptions() {
	return {
		items: rows,
		keyField: 'id' as const,
		direction: 'vertical' as const,
		el: scrollerHtmlEl,
		flowMode: true,
		enabled: isVirtualized.value,
	};
}

function createRowScroller() {
	if (fixedRowHeight != null) {
		const size = fixedRowHeight;
		const scroller = useRecycleScroller(() => ({
			...sharedScrollerOptions(),
			itemSize: size,
			minItemSize: size,
			typeField: 'type',
			buffer: 200,
			pageMode: false,
			prerender: 0,
			emitUpdate: false,
			updateInterval: 0,
		}));

		return { ...scroller, vRowItem: {} as ObjectDirective };
	}

	const scroller = useDynamicScroller(() => ({
		minItemSize: 40,
		...typeof virtualize === 'object' && virtualize ? virtualize : {},
		...sharedScrollerOptions(),
	}));

	return { ...scroller, vRowItem: scroller.vDynamicScrollerItem };
}

/**
 * Stable pool-slot key for a scroller view. Both scroller variants expose it
 * differently (`DynamicScrollerView.id` vs `View.nr.id`); keying by the pool
 * slot (not the item) is what lets the scroller reuse row instances instead of
 * remounting them as rows recycle.
 */
function getRowViewKey(view: { nr: { id: number } } | { id: number }): number {
	return 'nr' in view ? view.nr.id : view.id;
}

// Grow-only column width tracking while virtualized rows scroll. Kept off by
// default because most consumers prefer locked widths once the freeze window
// commits. When enabled, we rAF-batch per pool tick so rapid scroll events
// don't trigger a getBoundingClientRect per `<th>` on every frame — one
// measurement per animation frame is enough.
let growSampleFrame: number | null = null;

function scheduleGrowSample() {
	if (growSampleFrame != null) return;

	growSampleFrame = requestAnimationFrame(() => {
		growSampleFrame = null;
		const root = headerRef.value;
		if (!root) return;

		const updates: Record<string, number> = {};

		for (const th of root.querySelectorAll<HTMLElement>('th[data-column-id]')) {
			const columnId = th.dataset.columnId;

			if (!columnId || !isColumnAutoSized(columnId)) {
				continue;
			}

			updates[columnId] = th.getBoundingClientRect().width;
		}

		growFrozenColumnWidths(updates);
	});
}

watch(pool, () => {
	if (!isVirtualized.value || !virtualizeAutoSizeOnScroll) return;
	if (!headerRef.value) return;
	scheduleGrowSample();
});

onScopeDispose(() => {
	if (growSampleFrame != null) {
		cancelAnimationFrame(growSampleFrame);
		growSampleFrame = null;
	}
});

/**
 * Removes the `grid-cols-subgrid` utility from a class string so virtualized
 * rows/row-groups can declare explicit grid tracks via inline styles instead
 * of inheriting the parent table grid (which is not contiguous across the
 * dynamic scroller spacer tbodies).
 *
 * Only stripped once the freeze window has committed. While the layout phase
 * is still `relaxed`, subgrid is kept so `max-content` grid sizing propagates
 * across every rendered row, letting the freeze snapshot real column widths.
 */
function stripSubgridClasses(className: string, strip: boolean): string {
	if (!strip) return className;
	return className.split(/\s+/).filter((token) => token !== 'grid-cols-subgrid').join(' ');
}

/** True once virtualized rows should declare explicit grid tracks (post-freeze). */
const useExplicitBodyTracks = computed(() => isVirtualized.value && layoutPhase.value === 'frozen');

/**
 * Dependencies that can change a virtual row's measured height.
 */
function getVirtualRowSizeDependencies(row: Row<T>) {
	return [
		cssTableCols.value,
		row.getIsExpanded(),
		layoutPhase.value,
		rootBounding.width.value,
	];
}

/**
 * A cell's `style` and `class` depend only on its column (pin offset, grid track,
 * pinned variant), not on the row data — so recycling a pooled row to new data
 * must not force Vue to re-patch them. These maps hand back a stable reference
 * per column; they rebuild only when column layout actually changes (widths,
 * pinning, order, or the horizontal window), which is when the values change.
 */
const cellStyleByColumn = computed(() => {
	const styles = new Map<string, CSSProperties>();
	const windowed = columnWindowActive.value;

	for (const header of visibleColumns.value) {
		const base = getColumnStyles(header.column);

		styles.set(header.id, windowed
			? { ...base, gridColumn: String(getColumnGridIndex(header.id)) }
			: base);
	}

	return styles;
});

const cellClassByColumn = computed(() => {
	const classes = new Map<string, string>();

	for (const header of visibleColumns.value) {
		// A `td` class that is a function of the cell can't be precomputed per
		// column; those columns fall back to per-cell computation in getCellClass.
		if (typeof header.column.columnDef.meta?.class?.td === 'function') continue;

		classes.set(header.id, ui.value.td({
			class: [uiProp?.td, header.column.columnDef.meta?.class?.td],
			pinned: !!header.column.getIsPinned(),
		}));
	}

	return classes;
});

function getCellStyle(columnId: string): CSSProperties | undefined {
	return cellStyleByColumn.value.get(columnId);
}

function getCellClass(cell: Cell<T, unknown>): string {
	const cached = cellClassByColumn.value.get(cell.column.id);
	if (cached != null) return cached;

	return ui.value.td({
		class: [uiProp?.td, callOrReturn(cell.column.columnDef.meta?.class?.td, cell)],
		pinned: !!cell.column.getIsPinned(),
	});
}
</script>

<template>
	<DefineRowTemplate v-slot="{ row }">
		<tbody
			role="presentation"
			:class="stripSubgridClasses(ui.tbody({ class: [uiProp?.tbody] }), useExplicitBodyTracks)"
			:style="useExplicitBodyTracks ? { 'grid-template-columns': cssTableCols } : undefined"
		>
			<tr
				data-row
				:data-first-row="isFirstRow(row)"
				:data-selected="row.getIsSelected()"
				:data-selectable="!!onSelect"
				:data-expanded="row.getIsExpanded()"
				:data-striped="striped && row.index % 2 === 1"
				:role="onSelect ? 'button' : undefined"
				:tabindex="onSelect ? 0 : undefined"
				class="group/row relative"
				:class="stripSubgridClasses(ui.tr({ class: [
					uiProp?.tr,
					callOrReturn(tableApi.options.meta?.class?.tr, row),
				] }), useExplicitBodyTracks)"
				:style="useExplicitBodyTracks ? { 'grid-template-columns': cssTableCols } : undefined"
				@click="handleRowSelect(row, $event)"
			>
				<template v-for="cell in row.getVisibleCells()" :key="cell.column.id">
					<td
						v-if="isColumnCellRendered(cell.column.id)"
						class="group/cell"
						:class="[getCellClass(cell), getBottomEdgeClass(row, 'row')]"
						:style="getCellStyle(cell.column.id)"
						:data-pinned="cell.column.getIsPinned()"
						:data-last-left-pinned="cell.column.id === lastLeftPinnedColumnId || undefined"
						:data-column-id="cell.column.id"
						:data-auto-capped="autoCappedColumnIds.has(cell.column.id) || undefined"
						:data-relaxed="isVirtualized && isColumnRelaxed(cell.column.id) || undefined"
						:data-autofit-measuring="isColumnAutoFitting(cell.column.id) || undefined"
						@click="(e) => cell.column.columnDef.meta?.onCellClick?.(cell, e)"
						@contextmenu="(e) => cell.column.columnDef.meta?.onCellContextMenu?.(cell, e)"
					>
						<component :is="cell.column.columnDef.cell" v-bind="cell.getContext()" />
					</td>
				</template>
			</tr>
			<tr
				v-if="hasExpandedSlot && row.getIsExpanded()"
				data-expanded-content
				:class="stripSubgridClasses(ui.expandedRow({ class: [uiProp?.expandedRow] }), useExplicitBodyTracks)"
				:style="useExplicitBodyTracks ? { 'grid-template-columns': cssTableCols } : undefined"
			>
				<!--
					Full-width mode spans the whole row (grid-column 1 / -1) with a single cell, so
					the expanded content bleeds under the pinned leading columns. Used by non-scrolling
					tables where the pinned-track backdrop is unnecessary and a shaded panel must be
					full-bleed. Default mode keeps the pinned-aware split below.
				-->
				<td
					v-if="expandedFullWidth"
					:class="ui.expandedCell({ class: [uiProp?.expandedCell, 'col-span-full', getBottomEdgeClass(row, 'expanded')] })"
					@click.stop
				>
					<slot name="expanded" :row />
				</td>
				<template v-else>
					<td
						v-for="cell in row.getLeftVisibleCells()"
						:key="cell.id"
						aria-hidden="true"
						:class="ui.td({ class: [uiProp?.td, callOrReturn(cell.column.columnDef.meta?.class?.td, cell), getBottomEdgeClass(row, 'expanded')], pinned: true })"
						:style="getColumnStyles(cell.column)"
						:data-pinned="cell.column.getIsPinned()"
					/>
					<td
						:class="ui.expandedCell({ class: [uiProp?.expandedCell, getBottomEdgeClass(row, 'expanded')] })"
						:style="{ gridColumn: `span ${row.getCenterVisibleCells().length}` }"
						@click.stop
					>
						<slot name="expanded" :row />
					</td>
					<td
						v-for="cell in row.getRightVisibleCells()"
						:key="cell.id"
						aria-hidden="true"
						:class="ui.td({ class: [uiProp?.td, callOrReturn(cell.column.columnDef.meta?.class?.td, cell), getBottomEdgeClass(row, 'expanded')], pinned: true })"
						:style="getColumnStyles(cell.column)"
						:data-pinned="cell.column.getIsPinned()"
					/>
				</template>
			</tr>
		</tbody>
	</DefineRowTemplate>
	<DefineTableTemplate>
		<table
			ref="visibleTable"
			data-visible-table
			:data-layout-phase="isVirtualized ? layoutPhase : undefined"
			:class="ui.base({ class: [uiProp?.base] })"
			:style="{
				'--table-cols': cssTableCols,
				'--table-scroll-x': `${rootScroll.x.value}px`,
				'--data-table-auto-max-width': `${AUTO_COLUMN_MAX_WIDTH}px`,
				'grid-template-columns': cssTableCols,
			}"
		>
			<thead :class="ui.thead({ class: [uiProp?.thead] })">
				<tr ref="header" :class="ui.tr({ class: [uiProp?.tr] })">
					<DragDropProvider
						:sensors="dndSensors"
						@drag-start="handleDragStart"
						@drag-over="handleColumnDragOver"
						@drag-end="handleDragEnd"
					>
						<DDataTableHeaderCell
							v-for="(header, index) in visibleColumns"
							:key="header.id"
							:header
							:orderable="header.column.getCanReorder()"
							:orderable-index="orderableIndexMap.get(header.id) ?? -1"
							:sortable-group="columnPinGroupMap.get(header.id) ?? 'center'"
							:is-resizing-column
							:auto-capped="autoCappedColumnIds.has(header.id)"
							:relaxed="isVirtualized && isColumnRelaxed(header.id)"
							:autofit-measuring="isColumnAutoFitting(header.id)"
							:content-visible="headerContentVisible"
							:last-left-pinned="header.id === lastLeftPinnedColumnId"
							:header-element="headerRef"
							:class="ui.th({ class: [
									uiProp?.th,
									callOrReturn(header.column.columnDef.meta?.class?.th, header),
									(header.column.getCanReorder() ? 'cursor-pointer aria-selected:cursor-grabbing' : 'cursor-default'),
								],
								pinned: !!header.column.getIsPinned(),
							})"
							:column-styles="{ ...getColumnStyles(header.column), gridColumn: String(index + 1), gridRow: '1' }"
						>
							<slot :name="`${header.id}-header`" v-bind="header.getContext()">
								<FlexRender :render="header.column.columnDef.header" :props="header.getContext()" />
							</slot>
						</DDataTableHeaderCell>
						<!--
							Resize handles live as overlapping subgrid siblings of the header
							cells (not inside them) so they escape each cell's `overflow:hidden`
							clip and can straddle the column boundary. A generous, centered hit
							area is far easier to grab than a flush edge, and sitting above the
							cell lets it win the pointer over drag-to-reorder near the boundary. The
							hover line itself is centered on the column boundary so it coincides with
							the last-left-pinned column's scroll-shadow divider instead of poking past it.
						-->
						<template v-for="(header, index) in visibleColumns" :key="`resize-${header.id}`">
							<div
								v-if="canColumnResize(header.column)"
								class="relative h-full w-0 justify-self-end"
								:style="getResizeHandleStyle(header, index)"
							>
								<DTooltip :ui="{ content: 'h-auto flex-col' }">
									<div
										data-resize-handle
										role="presentation"
										:aria-label="$t('table.adjust_column_width')"
										class="
											absolute inset-y-0 inset-s-0 grid w-3 -translate-x-1/2 cursor-ew-resize touch-none place-items-center
											select-none
											after:h-full after:w-px after:translate-x-[-0.5px] after:bg-border after:opacity-0 after:transition
											group-hover/thead:after:opacity-100
											hover:after:w-(--drag-handle-width) hover:after:bg-primary-muted hover:after:opacity-100
											active:after:w-(--drag-handle-width) active:after:bg-primary-muted active:after:opacity-100
											rtl:translate-x-1/2
											rtl:after:translate-x-[0.5px]
										"
										@pointerdown.stop
										@dblclick.stop.prevent="onResizeDoubleClick(header)"
										@mousedown="onResizeMouseDown($event, header)"
										@touchstart="header.getResizeHandler()($event)"
									/>

									<template #content>
										<p>{{ $t('table.adjust_column_width') }}</p>
										<p>{{ $t('table.dbl_click_auto_width') }}</p>
									</template>
								</DTooltip>
							</div>
						</template>
					</DragDropProvider>
				</tr>
			</thead>
			<template v-if="isVirtualized">
				<tbody
					v-if="startSpacerSize > 0"
					class="col-span-full grid grid-cols-subgrid"
					aria-hidden="true"
					role="presentation"
					:style="{ height: `${startSpacerSize}px` }"
				/>

				<ReuseRowTemplate
					v-for="view in visiblePool"
					:key="getRowViewKey(view)"
					v-row-item="{
						view,
						sizeDependencies: getVirtualRowSizeDependencies(view.item),
					}"
					:row="view.item"
				/>

				<tbody
					v-if="endSpacerSize > 0"
					class="col-span-full grid grid-cols-subgrid"
					aria-hidden="true"
					role="presentation"
					:style="{ height: `${endSpacerSize}px` }"
				/>
			</template>

			<template v-else>
				<ReuseRowTemplate v-for="row in rows" :key="row.id" :row />
			</template>
		</table>
	</DefineTableTemplate>

	<Primitive
		ref="root"
		as="div"
		data-testid="data-table-scroll-root"
		:class="ui.root({ class: [className, uiProp?.root] })"
		v-bind="$attrs"
	>
		<div
			v-if="resizeIndicatorInsets"
			data-testid="data-table-resize-indicator"
			class="
				pointer-events-none absolute z-3 w-(--drag-handle-width) translate-x-[calc(-50%-0.5px)] touch-none bg-primary-muted
				rtl:translate-x-[calc(-50%+0.5px)]
			"
			role="presentation"
			:style="resizeIndicatorInsets"
		/>

		<!--
			Keep the table (and sticky thead) mounted across row-count transitions.
			Replacing it with the empty-state div on rows=0 unmounts <thead>, which
			caused a sticky-header position flash during nav between collections
			when rows briefly went empty while new data was loading. The empty state
			is rendered as a sibling so the `<table>` grid structure stays stable.
		-->
		<ReuseTableTemplate />
		<div
			v-if="rows.length === 0"
			:class="ui.empty({ class: [uiProp?.empty, 'sticky start-0 flex flex-1 items-center justify-center'] })"
		>
			<slot name="empty">
				{{ $t('table.no_data') }}
			</slot>
		</div>
	</Primitive>
</template>

<style scoped>
[data-pinned='left']:not(:has(~ [data-pinned='left'])) {
	border-right-width: clamp(1px, calc(var(--table-scroll-x) / 10), 2px);
}

/* Auto-sized max-content tracks need their cell contribution capped at the
 * automatic sizing ceiling. This applies throughout non-virtualized tables and
 * during the virtualized table's measurement window; frozen virtual tracks
 * already hold their capped width.
 *
 * `clip` and `min-width: 0` let the cell shrink; `max-width` clamps its track
 * contribution, so a long no-whitespace value cannot push a `max-content` track
 * beyond the ceiling. The track keeps its `minmax(minSize, …)` floor; the cell
 * handles the ceiling. `clip` also keeps overflow out of neighbouring columns.
 *
 * Auto-fitting columns are excluded: an explicit "fit to content" must measure
 * the full, uncapped width, so they stay `visible` with an unclamped track. */
:deep(th[data-auto-capped]:not([data-autofit-measuring])),
td[data-auto-capped]:not([data-autofit-measuring]) {
	overflow: clip;
	min-width: 0;
	max-width: var(--data-table-auto-max-width, 400px);
}

:deep(th[data-auto-capped][data-autofit-measuring]),
td[data-auto-capped][data-autofit-measuring] {
	overflow: visible;
	min-width: 0;
}

/* Auto-sized header cells should match the listing row height. Without this,
 * non-virtualized listings retain the default padded header height while their
 * virtualized counterparts use the configured row height. Body cells stay
 * unclamped so variable-height rows do not collapse. */
:deep(th[data-auto-capped]) {
	max-height: var(--data-table-row-height, 45px);
}
</style>
